import { useApolloClient } from "@apollo/client";
import {
  Button,
  ButtonGroup,
  Card,
  Divider,
  Menu,
  MenuItem,
  Popover,
} from "@blueprintjs/core";
import { useAuthState } from "@package/ui-authentication";
import { useToaster } from "@package/ui-components";
import {
  GetScheduledEventDocument,
  GetScheduledEventsDocument,
  useDeleteScheduledEventMutation,
  useGetScheduledEventQuery,
} from "@package/ui-gql/index-data-access";
import { useOrganizationState } from "@package/ui-organization";
import axios from "axios";
import { useLocation, useNavigate } from "react-router";
import {
  ITEM_GRID_PATH_EDIT,
  ITEM_GRID_PATH_EDIT_SCHEDULER_GROUP,
  ITEM_GRID_PATH_NEW,
  SidebarEntryPath,
} from "../../../../constants/contants";
import {
  setSchedulerArgsCreate,
  setSchedulerSelectedEventId,
} from "../../../../store/slices/slice-scheduler";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
const { ENV_VAR_API_URI_BASE } = import.meta.env;

export function SchedulerToolbar() {
  const { search } = useLocation();
  const { toaster } = useToaster();
  const { token } = useAuthState();
  const { selectedOrganization } = useOrganizationState();
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedEventId = useAppSelector(
    (state) => state.scheduler.selectedEventId,
  );

  const { data } = useGetScheduledEventQuery({
    variables: {
      id: selectedEventId ?? "",
    },
    skip: !selectedEventId,
  });
  const [deleteEvent] = useDeleteScheduledEventMutation();

  return (
    <Card compact elevation={1}>
      <div className="flex flex-row grow">
        <h4 className="m-0 content-center bp6-heading">Canvas Schedule</h4>
        <div className="flex grow justify-end">
          <Button
            minimal
            intent="primary"
            text="Create Event"
            icon="new-object"
            onClick={() => {
              dispatch(setSchedulerSelectedEventId());
              dispatch(setSchedulerArgsCreate());
              navigate(
                `/${SidebarEntryPath.scheduler}/${ITEM_GRID_PATH_NEW}${search}`,
              );
            }}
          />
          <Divider />
          <ButtonGroup>
            <Button
              minimal
              intent="warning"
              text="Edit Event"
              icon="edit"
              disabled={!selectedEventId}
              onClick={() => {
                dispatch(setSchedulerSelectedEventId());
                navigate(
                  `/${SidebarEntryPath.scheduler}/${ITEM_GRID_PATH_EDIT}/${selectedEventId}${search}`,
                );
              }}
            />
            <Popover
              placement="bottom"
              minimal
              content={
                <Menu>
                  <MenuItem
                    disabled={!data?.item?.groupId}
                    icon="multi-select"
                    text="Edit all recurring events"
                    onClick={() => {
                      dispatch(setSchedulerSelectedEventId());
                      navigate(
                        `/${SidebarEntryPath.scheduler}/${ITEM_GRID_PATH_EDIT_SCHEDULER_GROUP}/${selectedEventId}${search}`,
                      );
                    }}
                  />
                </Menu>
              }
            >
              <Button minimal intent="warning" icon="caret-down" disabled />
            </Popover>
          </ButtonGroup>
          <Divider />
          <ButtonGroup>
            <Button
              minimal
              intent="danger"
              text="Delete Event"
              icon="trash"
              disabled={!selectedEventId}
              onClick={() => {
                const toastId = toaster?.show({
                  icon: "trash",
                  intent: "none",
                  message: `Deleting Event`,
                  timeout: 3000,
                });

                deleteEvent({
                  notifyOnNetworkStatusChange: true,
                  refetchQueries: [
                    GetScheduledEventDocument,
                    GetScheduledEventsDocument,
                  ],
                  variables: { id: selectedEventId ?? "" },
                  onCompleted: () => {
                    dispatch(setSchedulerSelectedEventId());

                    if (toastId) {
                      toaster?.dismiss(toastId);
                    }
                    toaster?.show({
                      icon: "tick",
                      intent: "success",
                      message: `Deleted Event`,
                      timeout: 3000,
                    });
                  },
                });
              }}
            />
            <Popover
              placement="bottom"
              minimal
              content={
                <Menu>
                  <MenuItem
                    disabled={!data?.item?.groupId}
                    icon="multi-select"
                    text="Delete all recurring events"
                    onClick={async () => {
                      toaster?.show({
                        icon: "trash",
                        intent: "none",
                        message: `Deleting all recurring events`,
                        timeout: 3000,
                      });

                      try {
                        await axios
                          .create({ baseURL: ENV_VAR_API_URI_BASE })
                          .delete(
                            `/api/scheduled-event/group/${data?.item?.groupId}`,
                            {
                              timeout: 60 * 1000,
                              headers: {
                                Authorization: `Bearer ${token}`,
                                "x-org-id": selectedOrganization?.orgId,
                              },
                            },
                          );

                        dispatch(setSchedulerSelectedEventId());

                        client.refetchQueries({
                          include: [
                            GetScheduledEventDocument,
                            GetScheduledEventsDocument,
                          ],
                        });

                        toaster?.show({
                          icon: "tick",
                          intent: "success",
                          message: `Deleted all recurring events`,
                          timeout: 3000,
                        });
                      } catch (e: unknown) {
                        console.error("[SchedulerToolbar]: Error: ", e);
                        toaster?.show({
                          icon: "warning-sign",
                          intent: "danger",
                          message: `Failed deleting all recurring events`,
                          timeout: 3000,
                        });
                      }
                    }}
                  />
                </Menu>
              }
            >
              <Button
                minimal
                intent="danger"
                icon="caret-down"
                disabled={!data?.item?.groupId}
              />
            </Popover>
          </ButtonGroup>
        </div>
      </div>
    </Card>
  );
}
